<template>
  <model-form
    ref="modelForm"
    :model="clientVersion"
  >
    <div class="q-gutter-sm">
      <div class="row q-col-gutter-md items-start">
        <q-input
          v-model="clientVersion.version"
          class="col-4"
          label="Version (YYYY.version.patch.{note})"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-model="clientVersion.title"
          class="col-6"
          label="Title"
          bottom-slots
        />
        <q-checkbox
          v-model="clientVersion.urgent"
          class="col-2"
          label="Urgent"
          color="negative"
        >
          <q-tooltip>
            An urgent update that requires an update even if outside of the maintenance window
          </q-tooltip>
        </q-checkbox>
      </div>
      <div class="row q-col-gutter-md items-center">
        <q-select
          v-model="clientVersion.regions"
          class="col-4"
          filled
          multiple
          :options="REGIONS.ALL"
          use-chips
          stack-label
          label="Regions"
        />
        <q-checkbox
          v-model="clientVersion.adminRelated"
          class="col"
          label="Admin"
        />
        <q-checkbox
          v-model="clientVersion.posRelated"
          class="col"
          label="Point of Sale"
        />
        <q-checkbox
          v-model="clientVersion.consumerRelated"
          class="col"
          label="Consumer"
        />
        <q-checkbox
          v-model="clientVersion.signageRelated"
          class="col"
          label="Signage"
        />
        <q-checkbox
          v-model="clientVersion.kioskRelated"
          class="col"
          label="Kiosk"
        />
      </div>
      <div class="row q-gutter-md items-start q-mt-lg">
        <q-toggle
          class="col"
          :model-value="!!clientVersion.releaseNotes"
          label="Has Release Notes"
          @update:model-value="!!clientVersion.releaseNotes ? (clientVersion.releaseNotes = null) : (clientVersion.releaseNotes = ' ')"
        />
        <q-checkbox
          v-if="!!clientVersion.releaseNotes"
          v-model="clientVersion.important"
          class="col"
          label="Important"
          color="warning"
        >
          <q-tooltip>
            An important update will display the release notes to the user after the update. An low importants release note will not trigger the release notes dialog
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-if="!!clientVersion.releaseNotes"
          v-model="clientVersion.indyOnly"
          class="col-2"
          color="negative"
          label="INDY Only"
        >
          <q-tooltip>
            Only show release notes to INDY Super Users
          </q-tooltip>
        </q-checkbox>
        <q-checkbox
          v-if="!!clientVersion.releaseNotes"
          v-model="clientVersion.releaseNotesPublished"
          class="col"
          label="Release Notes Published"
        />
      </div>
      <template v-if="clientVersion.releaseNotes">
        <div class="row items-start">
          <q-editor
            v-model="clientVersion.releaseNotes"
            class="internal-documentation"
            :dense="$q.screen.lt.md"
            :definitions="{
              insert_img: {
                tip: 'Insert Image',
                icon: 'photo',
                handler: insertImage,
                disable: insertImageIsDisabled
              }
            }"
            :toolbar="[
              [
                {
                  label: $q.lang.editor.align,
                  icon: $q.iconSet.editor.align,
                  fixedLabel: true,
                  list: 'only-icons',
                  options: ['left', 'center', 'right', 'justify']
                }
              ],
              ['insert_img'],
              ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript'],
              ['token', 'hr', 'link', 'custom_btn'],
              ['print', 'fullscreen'],
              [
                {
                  label: $q.lang.editor.formatting,
                  icon: $q.iconSet.editor.formatting,
                  list: 'no-icons',
                  options: [
                    'p',
                    'h1',
                    'h2',
                    'h3',
                    'h4',
                    'h5',
                    'h6',
                    'code'
                  ]
                },
                {
                  label: $q.lang.editor.fontSize,
                  icon: $q.iconSet.editor.fontSize,
                  fixedLabel: true,
                  fixedIcon: true,
                  list: 'no-icons',
                  options: [
                    'size-1',
                    'size-2',
                    'size-3',
                    'size-4',
                    'size-5',
                    'size-6',
                    'size-7'
                  ]
                },
                {
                  label: $q.lang.editor.defaultFont,
                  icon: $q.iconSet.editor.font,
                  fixedIcon: true,
                  list: 'no-icons',
                  options: [
                    'default_font',
                    'arial',
                    'arial_black',
                    'comic_sans',
                    'courier_new',
                    'impact',
                    'lucida_grande',
                    'times_new_roman',
                    'verdana'
                  ]
                },
                'removeFormat'
              ],
              ['quote', 'unordered', 'ordered', 'outdent', 'indent'],

              ['undo', 'redo'],
              ['viewsource']
            ]"
            :fonts="{
              arial: 'Arial',
              arial_black: 'Arial Black',
              comic_sans: 'Comic Sans MS',
              courier_new: 'Courier New',
              impact: 'Impact',
              lucida_grande: 'Lucida Grande',
              times_new_roman: 'Times New Roman',
              verdana: 'Verdana'
            }"
          />
        </div>
      </template>
    </div>
  </model-form>
</template>

<script>

import { imgixHost } from 'src/config'
import { REGIONS } from 'src/models/developer/Developer.js'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

export default {
  name: 'ClientVersionForm',
  components: {
    ModelForm
  },
  models: {
    clientVersion: {
      releaseNotes: true,
      regions: true
    }
  },
  created () {
    _.extend(this, { REGIONS })
  },
  methods: {
    insertImage () {
      const selectedNode = window.getSelection().baseNode
      if (selectedNode.parentNode.closest('.q-editor__content') || selectedNode.closest('.q-editor__content')) {
        // create an input file element to open file dialog
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = async _ => {
          const imageLoading = document.createElement('div')
          imageLoading.textContent = 'Image Loading...'
          selectedNode.append(imageLoading)
          const files = Array.from(input.files)
          const file = files[0]
          const { key } = await this.$directUpload.uploadFile(file)
          const imageElement = document.createElement('img')
          imageElement.src = imgixHost + '/' + key + '?max-w=1000&fit=fill&auto=format,compress'
          imageElement.style = 'max-width: 100%;'
          selectedNode.append(imageElement)
          imageLoading.remove()
          const content = selectedNode.parentNode.closest('.q-editor__content') || selectedNode.closest('.q-editor__content')
          this.clientVersion.content = content.innerHTML.toString()
        }
        input.click()
      }
    },
    insertImageIsDisabled () {
      const selectedNode = window.getSelection().baseNode
      return typeof selectedNode?.append !== 'function'
    },
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    }
  }
}

</script>

<style lang="scss" scoped>
  .banner-img {
    width: 100%;
  }
</style>
